import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { mapTransferIdToApiPathParam } from 'api/warehouse/fcTransfers';
import { ViewEntityHeader } from 'components/entityViewer/ViewEntityHeader';
import { ViewEntityPage } from 'components/entityViewer/ViewEntityPage';
import { FCTransferCreateInfoCard } from 'components/warehouse/components/fcTransfers/create/FCTransferCreateInfoCard';
import { FCTransferCreateItemsTable } from 'components/warehouse/components/fcTransfers/create/FCTransferCreateItemsTable';
import {
    FCTransferLotPickerRow,
    getFCTransferInventoryRowKey,
} from 'components/warehouse/components/fcTransfers/create/FCTransferLotPickerRow';
import { SelectedTransferLotSummary } from 'components/warehouse/components/fcTransfers/create/SelectedTransferLotSummary';
import {
    defaultQuantitiesFromInventoryRow,
    newFCTransferLineItemClientId,
    readLotAndLocationIds,
} from 'components/warehouse/components/fcTransfers/fcTransferLineItemUtils';
import {
    useAvailableTransferLots,
    useCreateFCTransfer,
} from 'components/warehouse/components/fcTransfers/queries/FCTransferQueries';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { useFacilitiesQuery } from 'shared/hooks/warehouse/useFacilitiesQuery';

const FC_TRANSFERS_LIST_HREF = '/warehouse/fc-transfers';

/**
 * @param {unknown} facilityId
 * @returns {number | null}
 */
function normalizePositiveFacilityIdFromUser(facilityId) {
    if (facilityId === undefined || facilityId === null || facilityId === '') {
        return null;
    }
    const n = Number(facilityId);
    return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * @param {Record<string, unknown> | null | undefined} facility
 * @returns {number | null}
 */
function facilityIdFromPicker(facility) {
    if (facility == null || typeof facility !== 'object') return null;
    const { id } = /** @type {{ id?: unknown }} */ (facility);
    if (id === undefined || id === null || id === '') return null;
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * FC Transfer create page: primary fields, lot picker, line-item table, save/cancel (tech spec §6.3–§6.5).
 */
export function CreateFCTransfer() {
    const history = useHistory();
    const { user } = useContext(UserContext);

    const sourceFacilityId = useMemo(() => normalizePositiveFacilityIdFromUser(user?.facilityID), [user?.facilityID]);

    const { data: facilities = [], isLoading: facilitiesLoading } = useFacilitiesQuery();

    const sourceFacility = useMemo(() => {
        if (sourceFacilityId == null) return null;
        return facilities.find((f) => Number(f.id) === sourceFacilityId) ?? null;
    }, [facilities, sourceFacilityId]);

    const [destinationFacility, setDestinationFacility] = useState(
        /** @type {Record<string, unknown> | null} */ (null),
    );
    const [notes, setNotes] = useState('');
    const [estimatedDepartureDate, setEstimatedDepartureDate] = useState('');
    const [selectedInventoryRow, setSelectedInventoryRow] = useState(
        /** @type {Record<string, unknown> | null} */ (null),
    );
    const [lineItems, setLineItems] = useState([]);

    const [fieldErrors, setFieldErrors] = useState(/** @type {Record<string, string>} */ ({}));
    const [lineItemsError, setLineItemsError] = useState(/** @type {string | null} */ (null));
    const [showValidation, setShowValidation] = useState(false);
    const [saveError, setSaveError] = useState(/** @type {string | null} */ (null));
    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

    const { data: availableRows = [], isFetching: isLotsLoading } = useAvailableTransferLots({
        facilityId: sourceFacilityId,
        enabled: Boolean(sourceFacilityId),
    });

    const addedLotLocationKeys = useMemo(() => {
        const s = new Set();
        for (const li of lineItems) {
            s.add(`${Number(li.lotId)}:${Number(li.locationId)}`);
        }
        return s;
    }, [lineItems]);

    const pickableInventoryRows = useMemo(() => {
        if (!Array.isArray(availableRows)) return [];
        return availableRows.filter((row) => {
            const k = getFCTransferInventoryRowKey(row);
            if (!k) return false;
            return !addedLotLocationKeys.has(k);
        });
    }, [addedLotLocationKeys, availableRows]);

    useEffect(() => {
        if (selectedInventoryRow == null) return;
        const k = getFCTransferInventoryRowKey(selectedInventoryRow);
        if (!k || !addedLotLocationKeys.has(k)) return;
        setSelectedInventoryRow(null);
    }, [addedLotLocationKeys, selectedInventoryRow]);

    const createMutation = useCreateFCTransfer({
        onSuccess: (transfer) => {
            const id = transfer?.id ?? transfer?.transferId;
            const pathId = mapTransferIdToApiPathParam(id);
            if (pathId !== undefined) {
                history.push(`/warehouse/fc-transfers/${encodeURIComponent(String(pathId))}`);
            } else {
                history.push(FC_TRANSFERS_LIST_HREF);
            }
        },
        onError: (err) => {
            setSaveError(err?.message ?? 'Failed to create transfer');
        },
    });

    const isDirty = useMemo(() => {
        if (destinationFacility != null) return true;
        if (notes.trim().length > 0) return true;
        if (estimatedDepartureDate.trim().length > 0) return true;
        if (lineItems.length > 0) return true;
        if (selectedInventoryRow != null) return true;
        return false;
    }, [destinationFacility, estimatedDepartureDate, lineItems.length, notes, selectedInventoryRow]);

    const locationBadges = useMemo(() => {
        if (selectedInventoryRow == null || !Array.isArray(availableRows)) return undefined;
        const lotId = selectedInventoryRow.lotId ?? selectedInventoryRow.lot_id;
        if (lotId === undefined || lotId === null) return undefined;
        const lotKey = String(lotId);
        const matches = availableRows.filter((r) => String(r.lotId ?? r.lot_id ?? '') === lotKey);
        if (matches.length <= 1) return undefined;
        const filtered = matches.filter((r) => {
            const rowKey = getFCTransferInventoryRowKey(r);
            return Boolean(rowKey) && !addedLotLocationKeys.has(rowKey);
        });
        if (filtered.length <= 1) return undefined;
        return filtered.map((r, i) => ({
            id: String(r.warehouseLocationId ?? r.warehouse_location_id ?? i),
            label: String(r.locationName ?? r.location_name ?? 'Location'),
            numCases: r.numCases ?? r.num_cases ?? 0,
        }));
    }, [addedLotLocationKeys, availableRows, selectedInventoryRow]);

    const resetAllFormState = useCallback(() => {
        setDestinationFacility(null);
        setNotes('');
        setEstimatedDepartureDate('');
        setSelectedInventoryRow(null);
        setLineItems([]);
        setFieldErrors({});
        setLineItemsError(null);
        setShowValidation(false);
        setSaveError(null);
    }, []);

    const requestNavigateAway = useCallback(() => {
        if (!isDirty) {
            resetAllFormState();
            history.push(FC_TRANSFERS_LIST_HREF);
            return;
        }
        setDiscardDialogOpen(true);
    }, [history, isDirty, resetAllFormState]);

    const confirmDiscardAndLeave = useCallback(() => {
        resetAllFormState();
        setDiscardDialogOpen(false);
        history.push(FC_TRANSFERS_LIST_HREF);
    }, [history, resetAllFormState]);

    const handleBackLinkClick = useCallback(
        (e) => {
            if (isDirty) {
                e.preventDefault();
                setDiscardDialogOpen(true);
            }
        },
        [isDirty],
    );

    const handleAddLot = useCallback(() => {
        if (selectedInventoryRow == null) return;
        const ids = readLotAndLocationIds(selectedInventoryRow);
        if (!ids) return;
        const pairKey = `${ids.lotId}:${ids.locationId}`;
        if (addedLotLocationKeys.has(pairKey)) {
            setLineItemsError('This lot and location are already on the transfer.');
            return;
        }
        const { numCases, numLooseUnits } = defaultQuantitiesFromInventoryRow(selectedInventoryRow);
        setLineItems((prev) => [
            ...prev,
            {
                clientId: newFCTransferLineItemClientId('create'),
                lotId: ids.lotId,
                locationId: ids.locationId,
                inventorySnapshot: { ...selectedInventoryRow },
                numCases,
                numLooseUnits,
            },
        ]);
        setSelectedInventoryRow(null);
        setLineItemsError(null);
    }, [addedLotLocationKeys, selectedInventoryRow]);

    const handleNumCasesChange = useCallback((clientId, numCases) => {
        setLineItems((prev) => prev.map((row) => (row.clientId === clientId ? { ...row, numCases } : row)));
    }, []);

    const handleNumLooseUnitsChange = useCallback((clientId, numLooseUnits) => {
        setLineItems((prev) => prev.map((row) => (row.clientId === clientId ? { ...row, numLooseUnits } : row)));
    }, []);

    const handleRemoveRow = useCallback((clientId) => {
        setLineItems((prev) => prev.filter((row) => row.clientId !== clientId));
    }, []);

    const validateForSubmit = useCallback(() => {
        const errors = {};
        const srcId = sourceFacilityId;
        const dstId = facilityIdFromPicker(destinationFacility);
        if (srcId == null) {
            errors.sourceFacilityId = 'Your account must have a source facility to create a transfer';
        }
        if (dstId == null) errors.destinationFacilityId = 'Destination FC is required';
        if (!estimatedDepartureDate.trim()) {
            errors.estimatedDepartureDate = 'Estimated departure date is required';
        }
        if (srcId != null && dstId != null && srcId === dstId) {
            errors.destinationFacilityId = 'Destination must be different from source';
        }

        let itemsErr = null;
        if (lineItems.length === 0) {
            itemsErr = 'Add at least one line item before saving.';
        } else {
            const seenPairs = new Set();
            let duplicatePair = false;
            for (const li of lineItems) {
                const pairKey = `${Number(li.lotId)}:${Number(li.locationId)}`;
                if (seenPairs.has(pairKey)) {
                    duplicatePair = true;
                    break;
                }
                seenPairs.add(pairKey);
            }
            if (duplicatePair) {
                itemsErr = 'Remove duplicate lot and location rows before saving.';
            } else {
                const invalidQty = lineItems.some((li) => {
                    const c = Math.max(0, Math.floor(Number(li.numCases) || 0));
                    const u = Math.max(0, Math.floor(Number(li.numLooseUnits) || 0));
                    return c <= 0 && u <= 0;
                });
                if (invalidQty) {
                    itemsErr = 'Each line needs at least one case or one loose unit.';
                }
            }
        }

        setFieldErrors(errors);
        setLineItemsError(itemsErr);
        setShowValidation(true);
        return Object.keys(errors).length === 0 && itemsErr == null;
    }, [destinationFacility, estimatedDepartureDate, lineItems, sourceFacilityId]);

    const handleSave = useCallback(() => {
        setSaveError(null);
        if (!validateForSubmit()) return;

        const srcId = sourceFacilityId;
        const dstId = facilityIdFromPicker(destinationFacility);
        if (srcId == null || dstId == null) return;

        const items = lineItems.map((li) => ({
            lotId: li.lotId,
            locationId: li.locationId,
            numCases: Math.max(0, Math.floor(Number(li.numCases) || 0)),
            numLooseUnits: Math.max(0, Math.floor(Number(li.numLooseUnits) || 0)),
        }));

        createMutation.mutate({
            sourceFacilityId: srcId,
            destinationFacilityId: dstId,
            estimatedDepartureDate: estimatedDepartureDate.trim(),
            notes: notes.trim() || undefined,
            items,
        });
    }, [
        createMutation,
        destinationFacility,
        estimatedDepartureDate,
        lineItems,
        notes,
        sourceFacilityId,
        validateForSubmit,
    ]);

    const isSaving = createMutation.isPending;

    const lotPickerEmptyHelperText = useMemo(() => {
        if (!sourceFacilityId) {
            return 'Your account must have a facility assignment to load available lots.';
        }
        const total = Array.isArray(availableRows) ? availableRows.length : 0;
        if (total > 0 && pickableInventoryRows.length === 0) {
            return 'All eligible lot and location rows for this facility are already on the transfer. Remove a line item to add a different lot or change quantities.';
        }
        return 'No eligible inventory rows for this source facility.';
    }, [availableRows, pickableInventoryRows.length, sourceFacilityId]);

    const headerIdentification = (
        <div className="flex w-full min-w-0 flex-wrap items-start gap-3 lg:items-center">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">Create FC Transfer</h1>
            <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" disabled={isSaving} onClick={requestNavigateAway}>
                    Cancel
                </Button>
                <Button type="button" size="sm" disabled={isSaving} onClick={handleSave}>
                    Save Transfer
                </Button>
            </div>
        </div>
    );

    return (
        <div className="flex min-h-screen flex-col px-4 pb-8 pt-4">
            <ViewEntityPage
                header={
                    <ViewEntityHeader
                        backHref={FC_TRANSFERS_LIST_HREF}
                        backLabel="Back to FC Transfers"
                        backLinkProps={{ onClick: handleBackLinkClick }}
                        entityIdentificationComponent={headerIdentification}
                    />
                }
                primaryInformationCard={
                    <>
                        {saveError ? (
                            <Alert variant="destructive" className="mb-2">
                                <AlertTitle>Could not save transfer</AlertTitle>
                                <AlertDescription>{saveError}</AlertDescription>
                            </Alert>
                        ) : null}
                        <FCTransferCreateInfoCard
                            destinationFacility={destinationFacility}
                            estimatedDepartureDate={estimatedDepartureDate}
                            fieldErrors={fieldErrors}
                            lockedSourceFacilityId={sourceFacilityId}
                            notes={notes}
                            onDestinationFacilityChange={(f) => {
                                setDestinationFacility(f);
                                setFieldErrors((e) => ({ ...e, destinationFacilityId: '' }));
                            }}
                            onNotesChange={(v) => setNotes(v)}
                            onEstimatedDepartureDateChange={(v) => {
                                setEstimatedDepartureDate(v);
                                setFieldErrors((e) => ({ ...e, estimatedDepartureDate: '' }));
                            }}
                            showValidation={showValidation}
                            sourceFacility={sourceFacility}
                            sourceFacilityLoading={facilitiesLoading}
                            sourceFacilityReadOnly
                        />
                    </>
                }
                additionalInformationCards={
                    <Card>
                        <CardHeader className="pb-0">
                            <CardTitle className="text-base font-medium">Line items</CardTitle>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-6 pt-6">
                            <FCTransferLotPickerRow
                                rows={pickableInventoryRows}
                                selectedRow={selectedInventoryRow}
                                onSelectedRowChange={setSelectedInventoryRow}
                                onAddLot={handleAddLot}
                                isLoading={Boolean(sourceFacilityId) && isLotsLoading}
                                addLotDisabled={isSaving}
                                emptyHelperText={lotPickerEmptyHelperText}
                            />
                            <SelectedTransferLotSummary
                                selectedRow={selectedInventoryRow}
                                locationBadges={locationBadges}
                            />
                            {lineItemsError ? (
                                <p className="text-sm text-destructive" role="alert">
                                    {lineItemsError}
                                </p>
                            ) : null}
                            <FCTransferCreateItemsTable
                                embedded
                                items={lineItems}
                                disabled={isSaving}
                                onNumCasesChange={handleNumCasesChange}
                                onNumLooseUnitsChange={handleNumLooseUnitsChange}
                                onRemoveRow={handleRemoveRow}
                            />
                        </CardContent>
                    </Card>
                }
            />

            <Dialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Discard unsaved changes?</DialogTitle>
                        <DialogDescription>
                            You have unsaved changes on this page. If you leave now, they will be lost.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDiscardDialogOpen(false)}>
                            Keep editing
                        </Button>
                        <Button type="button" variant="destructive" onClick={confirmDiscardAndLeave}>
                            Discard and leave
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

CreateFCTransfer.displayName = 'CreateFCTransfer';
